import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Award, Upload, Image as ImageIcon, FileText, Video } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import CertificateTemplateModal from '../components/certificate/CertificateTemplateModal';
import CertificatePreview from '../components/certificate/CertificatePreview';
import { isPortraitTemplate } from '../components/certificate/templateConfigs';
import ClassThumbnailCropModal from '../components/class/ClassThumbnailCropModal';
import ClassCategorySelect from '../components/class/ClassCategorySelect';
import RichTextEditor from '../components/RichTextEditor';
import AddressSearch from '../components/AddressSearch';
import type { CreateClassData, LocationType } from '../types/class';

const toggleId = (arr: string[], id: string) =>
  arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

export default function ProviderClassCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 인증서 템플릿 모달 상태
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [certificateTemplateId, setCertificateTemplateId] = useState<string | null>(null);
  const [certificateTemplateName, setCertificateTemplateName] = useState<string | null>(null);
  const [savedTemplateData, setSavedTemplateData] = useState<any>(null);

  // 썸네일 상태
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [isThumbnailCropModalOpen, setIsThumbnailCropModalOpen] = useState(false);
  const [tempThumbnailUrl, setTempThumbnailUrl] = useState<string | null>(null);
  const [isSavingThumbnail, setIsSavingThumbnail] = useState(false);

  // 강사 상태 (우수 인증 교육 기관용)
  const [instructors, setInstructors] = useState<any[]>([]);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>([]);

  // 교육 카테고리 상태
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categoryComplete, setCategoryComplete] = useState(false);

  // 폼 상태
  const [formData, setFormData] = useState<CreateClassData>({
    title: '',
    price: 0,
    discountPrice: 0,
    startDate: '',
    endDate: '',
    enrollmentStartDate: '',
    enrollmentEndDate: '',
    scheduleInfo: '',
    locationType: 'OFFLINE',
    locationAddress: '',
    offlineHours: 0,
    onlineHours: 0,
    classType: 'CERTIFICATE',
    capacity: 10,
    description: '',
    curriculum: '',
    youtubeUrl: '',
    certificateTemplateId: undefined,
  });

  // 우수 인증 교육 기관의 강사 목록 로드
  useEffect(() => {
    const fetchInstructors = async () => {
      if (!user?.is_premium) return;

      try {
        setInstructorsLoading(true);
        const response = await fetch(`/api/v1/providers/${user.id}/instructors`, {
          credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to fetch instructors');

        const data = await response.json();
        setInstructors(data.instructors || []);
      } catch (error) {
        console.error('Failed to fetch instructors:', error);
        setInstructors([]);
      } finally {
        setInstructorsLoading(false);
      }
    };

    fetchInstructors();
  }, [user]);

  // 썸네일 파일 선택 핸들러
  const handleThumbnailFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 크기 제한 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB를 초과할 수 없습니다.');
      return;
    }

    // 이미지 파일 확인
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    // 파일을 Object URL로 변환하여 크롭 모달에 전달
    const url = URL.createObjectURL(file);
    setTempThumbnailUrl(url);
    setIsThumbnailCropModalOpen(true);
  };

  // 썸네일 크롭 후 저장 핸들러
  const handleSaveThumbnail = async (croppedImage: Blob) => {
    setIsSavingThumbnail(true);
    try {
      // 임시로 blob URL 생성하여 미리보기 표시
      const blobUrl = URL.createObjectURL(croppedImage);
      setThumbnailUrl(blobUrl);
      setIsThumbnailCropModalOpen(false);

      // 실제로는 수업 등록 시 함께 업로드
      // formData에 thumbnailUrl 저장은 수업 등록 성공 후 업로드 API 호출 시 처리
    } catch (error) {
      console.error('Error saving thumbnail:', error);
      alert('썸네일 저장에 실패했습니다.');
    } finally {
      setIsSavingThumbnail(false);
    }
  };

  // 썸네일 삭제 핸들러
  const handleRemoveThumbnail = () => {
    if (thumbnailUrl) {
      URL.revokeObjectURL(thumbnailUrl);
    }
    setThumbnailUrl(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 유효성 검사
    if (!formData.title.trim()) {
      setError('수업명을 입력해주세요.');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      setError('수업 기간을 선택해주세요.');
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setError('수업 종료일은 시작일과 같거나 이후여야 합니다.');
      return;
    }

    if (!formData.enrollmentStartDate || !formData.enrollmentEndDate) {
      setError('수강신청 기간을 선택해주세요.');
      return;
    }

    if (new Date(formData.enrollmentStartDate) >= new Date(formData.enrollmentEndDate)) {
      setError('수강신청 종료일은 시작일보다 이후여야 합니다.');
      return;
    }

    if (!formData.description.replace(/<[^>]*>/g, '').trim() && !formData.description.includes('<img')) {
      setError('수업 소개를 입력해주세요.');
      return;
    }

    if (formData.locationType !== 'ONLINE' && !formData.locationAddress?.trim()) {
      setError('오프라인/혼합 수업은 주소를 입력해주세요.');
      return;
    }

    if (!formData.classType) {
      setError('수업 유형을 선택해주세요.');
      return;
    }

    if (formData.discountPrice > formData.price) {
      setError('할인가는 정가보다 클 수 없습니다.');
      return;
    }

    if (!categoryId) {
      setError('교육 카테고리를 선택해주세요.');
      return;
    }

    if (!categoryComplete) {
      setError('교육 카테고리는 가장 하위 단계까지 선택해주세요.');
      return;
    }

    if (!certificateTemplateId) {
      setError('인증서 템플릿을 만들어주세요.');
      return;
    }

    setLoading(true);

    try {
      // 1. 수업 등록
      const response = await fetch('/api/v1/provider/classes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          instructorIds: selectedInstructorIds,
          categoryId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '수업 등록에 실패했습니다.');
      }

      const classId = data.class.id;

      // 2. 썸네일 업로드 (있는 경우)
      if (thumbnailUrl) {
        try {
          // Blob URL에서 Blob 가져오기
          const thumbnailBlob = await fetch(thumbnailUrl).then(r => r.blob());

          const uploadFormData = new FormData();
          uploadFormData.append('image', thumbnailBlob, 'thumbnail.jpg');

          const uploadResponse = await fetch(`/api/v1/provider/classes/${classId}/thumbnail`, {
            method: 'POST',
            credentials: 'include',
            body: uploadFormData,
          });

          if (!uploadResponse.ok) {
            console.error('Failed to upload thumbnail');
            // 썸네일 업로드 실패해도 수업은 등록되었으므로 계속 진행
          }
        } catch (uploadError) {
          console.error('Error uploading thumbnail:', uploadError);
          // 썸네일 업로드 실패해도 수업은 등록되었으므로 계속 진행
        }
      }

      // 3. 성공 시 게시물 목록으로 이동
      navigate('/provider/posts');
    } catch (err: any) {
      console.error('Failed to create class:', err);
      setError(err.message || '수업 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 총 수업시간 검증 후 인증서 템플릿 모달 열기
  const handleOpenTemplateModal = () => {
    const { locationType, offlineHours, onlineHours } = formData;
    if (locationType === 'OFFLINE' && !offlineHours) {
      alert('총 수업 시간(오프라인)을 먼저 입력해주세요.');
      return;
    }
    if (locationType === 'ONLINE' && !onlineHours) {
      alert('총 수업 시간(온라인)을 먼저 입력해주세요.');
      return;
    }
    if (locationType === 'HYBRID' && (!offlineHours || !onlineHours)) {
      alert('총 수업 시간(오프라인, 온라인)을 모두 먼저 입력해주세요.');
      return;
    }
    setIsTemplateModalOpen(true);
  };

  // 인증서 템플릿 저장 핸들러
  const handleSaveTemplate = async (templateData: any, templateId: string) => {
    try {
      // 모달에서 이미 API를 통해 저장되었으므로, 받은 ID를 사용
      setCertificateTemplateId(templateId);
      setCertificateTemplateName(templateData.name);
      setSavedTemplateData(templateData); // 미리보기용 데이터 저장
      setFormData((prev) => ({
        ...prev,
        certificateTemplateId: templateId,
      }));
      setIsTemplateModalOpen(false);
      console.log('Certificate template saved with ID:', templateId);
    } catch (error: any) {
      console.error('Failed to save certificate template:', error);
      alert('인증서 템플릿 저장에 실패했습니다.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        {/* 공유 사이드바 */}
        <AdminSidebar />

        {/* 메인 콘텐츠 */}
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div className="w-full">
            {/* 헤더 */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/provider/posts')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">게시물 목록으로 돌아가기</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">수업 등록</h1>
              <p className="text-gray-600 mt-1 text-sm">새로운 수업을 등록하고 학생들을 모집하세요</p>
            </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-soft overflow-hidden">
          {/* 2열 그리드 레이아웃 */}
          <div className="grid grid-cols-2 gap-6 p-5">
            {/* 첫 번째 열: 수업 정보 */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                수업 정보
              </h3>

              {/* 수업명 (전체 너비) */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                  수업명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="예: 필라테스 지도자 자격증 과정"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  required
                />
              </div>

              {/* 정가 & 할인가 (2단) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    정가 (원) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price === 0 ? '' : formData.price.toLocaleString('ko-KR')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value === '' || /^\d+$/.test(value)) {
                        const newPrice = value === '' ? 0 : parseInt(value);
                        setFormData((prev) => ({
                          ...prev,
                          price: newPrice,
                          // 할인가가 0이거나 정가보다 크면 자동 조정
                          discountPrice:
                            prev.discountPrice === 0 || prev.discountPrice > newPrice
                              ? newPrice
                              : prev.discountPrice,
                        }));
                      }
                    }}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">원래 가격</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    할인가 (원) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="discountPrice"
                    value={formData.discountPrice === 0 ? '' : formData.discountPrice.toLocaleString('ko-KR')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '');
                      if (value === '' || /^\d+$/.test(value)) {
                        setFormData((prev) => ({
                          ...prev,
                          discountPrice: value === '' ? 0 : parseInt(value),
                        }));
                      }
                    }}
                    placeholder="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm ${
                      formData.discountPrice > formData.price
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    실제 판매가 (정가 이하)
                    {formData.price > 0 && formData.discountPrice > 0 && formData.discountPrice < formData.price && (
                      <span className="ml-1 text-red-500 font-medium">
                        ({Math.round(((formData.price - formData.discountPrice) / formData.price) * 100)}% 할인)
                      </span>
                    )}
                  </p>
                  {formData.discountPrice > formData.price && (
                    <p className="text-xs text-red-500 mt-1">할인가는 정가보다 클 수 없습니다</p>
                  )}
                </div>
              </div>

              {/* 모집 인원 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                  모집 인원 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  min="1"
                  placeholder="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  required
                />
              </div>

              {/* 수업 시작일 & 종료일 (2단) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    수업 시작일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    수업 종료일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    required
                  />
                </div>
              </div>

              {/* 수강신청 시작일 & 종료일 (2단) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    수강신청 시작일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="enrollmentStartDate"
                    value={formData.enrollmentStartDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    수강신청 종료일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="enrollmentEndDate"
                    value={formData.enrollmentEndDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    required
                  />
                </div>
              </div>

              {/* 수업 일정 & 수업 장소 (2단) */}
              <div className="grid grid-cols-2 gap-3">
                {/* 수업 일정 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">수업 일정</label>
                  <input
                    type="text"
                    name="scheduleInfo"
                    value={formData.scheduleInfo}
                    onChange={handleChange}
                    placeholder="예: 매주 월/수/금 10:00-13:00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  />
                </div>

                {/* 수업 장소 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    수업 장소 <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="locationType"
                    value={formData.locationType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    required
                  >
                    <option value="OFFLINE">오프라인</option>
                    <option value="ONLINE">온라인</option>
                    <option value="HYBRID">혼합 (온라인 + 오프라인)</option>
                  </select>
                </div>
              </div>

              {/* 총 수업 시간 */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                  총 수업 시간 (시간) <span className="text-red-500">*</span>
                </label>
                <div className={`grid ${formData.locationType === 'HYBRID' ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                  {formData.locationType !== 'ONLINE' && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">오프라인 시간</label>
                      <input
                        type="text"
                        value={formData.offlineHours || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setFormData(prev => ({ ...prev, offlineHours: value === '' ? 0 : parseInt(value) }));
                          }
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        required
                      />
                    </div>
                  )}
                  {formData.locationType !== 'OFFLINE' && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">온라인 시간</label>
                      <input
                        type="text"
                        value={formData.onlineHours || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setFormData(prev => ({ ...prev, onlineHours: value === '' ? 0 : parseInt(value) }));
                          }
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* 수업 장소 주소 (오프라인/혼합인 경우만 표시) */}
              {formData.locationType !== 'ONLINE' && (
                <AddressSearch
                  value={formData.locationAddress}
                  onChange={(address) => setFormData(prev => ({ ...prev, locationAddress: address }))}
                  required
                />
              )}

              {/* 수업 유형 (전체 너비) */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                  수업 유형 <span className="text-red-500">*</span>
                </label>
                <select
                  name="classType"
                  value={formData.classType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  required
                >
                  <option value="CERTIFICATE">자격증</option>
                  <option value="COMPLETION">수료증</option>
                  <option value="WORKSHOP">워크샵</option>
                  <option value="OTHER">기타</option>
                </select>
              </div>

              {/* 교육 카테고리 (관리자 설정 분류) */}
              <ClassCategorySelect
                value={categoryId}
                onChange={(id, complete) => {
                  setCategoryId(id);
                  setCategoryComplete(complete);
                }}
                required
              />

              {/* 수업 소개 (전체 너비) */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                  수업 소개 <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  content={formData.description}
                  onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
                  placeholder="수업에 대한 상세한 설명을 작성해주세요"
                  minHeight="300px"
                />
              </div>

              {/* 커리큘럼 (전체 너비) */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">커리큘럼</label>
                <RichTextEditor
                  content={formData.curriculum}
                  onChange={(html) => setFormData((prev) => ({ ...prev, curriculum: html }))}
                  placeholder="수업 커리큘럼을 작성해주세요 (선택사항)"
                  minHeight="300px"
                />
              </div>

              {/* 강사 선택 (우수 인증 교육 기관만, 다중 선택 가능) */}
              {user?.is_premium && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    강사 <span className="text-gray-500 text-xs font-normal">(선택사항, 다중 선택 가능)</span>
                  </label>
                  {instructorsLoading ? (
                    <div className="animate-pulse h-10 bg-gray-200 rounded-lg"></div>
                  ) : instructors.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {instructors.map((instructor) => {
                        const active = selectedInstructorIds.includes(instructor.id);
                        return (
                          <button
                            key={instructor.id}
                            type="button"
                            onClick={() => setSelectedInstructorIds((prev) => toggleId(prev, instructor.id))}
                            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                              active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {instructor.name}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-xs">
                      등록된 강사가 없습니다. 우수 인증 교육 기관 관리에서 강사를 등록해주세요.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 두 번째 열: 소개 자료 + 인증서 */}
            <div className="space-y-6">
              {/* 소개 자료 섹션 */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  소개 자료
                </h3>

                {/* 수업 썸네일 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    수업 썸네일
                  </label>

                  {thumbnailUrl ? (
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 group">
                      <img
                        src={thumbnailUrl}
                        alt="수업 썸네일"
                        className="w-full h-full object-cover"
                      />
                      {/* 호버 시 삭제 버튼 */}
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <label className="px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-sm font-medium flex items-center gap-1.5">
                          <Upload className="w-4 h-4" />
                          변경
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleThumbnailFileSelect}
                            className="hidden"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handleRemoveThumbnail}
                          className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="block aspect-video rounded-lg border-2 border-dashed border-gray-300 hover:border-primary transition-colors cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="h-full flex flex-col items-center justify-center gap-2">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                        <span className="text-sm font-medium text-gray-600">썸네일 이미지 업로드</span>
                        <span className="text-xs text-gray-500">권장: 800 x 450px (16:9 비율) | 최대 5MB</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailFileSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">수업 목록과 상세 페이지에 표시될 썸네일입니다</p>
                </div>

                {/* 유튜브 영상 미리보기 */}
                {formData.youtubeUrl && (
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-900">
                    <iframe
                      src={formData.youtubeUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title="YouTube video preview"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                {/* 유튜브 링크 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                    유튜브 영상 링크
                  </label>
                  <input
                    type="url"
                    name="youtubeUrl"
                    value={formData.youtubeUrl}
                    onChange={handleChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">수업 소개 영상이 있으면 더 많은 수강생을 모집할 수 있습니다</p>
                </div>
              </div>

              {/* 인증서 섹션 */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  인증서
                </h3>

                {!savedTemplateData ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <Award className="w-12 h-12 mx-auto text-primary mb-3" />
                    <p className="text-sm font-medium text-gray-900 mb-1">수료증 발급 설정</p>
                    <p className="text-xs text-gray-600">수강생이 수업을 완료하면 자동으로 인증서가 발급됩니다</p>
                  </div>
                ) : (
                  /* 저장된 템플릿 미리보기 */
                  <div className="bg-white border-2 border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-800">
                          {savedTemplateData.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleOpenTemplateModal}
                        className="text-xs text-primary hover:underline"
                      >
                        수정
                      </button>
                    </div>
                    {/* 저장 시 캡처한 썸네일 이미지로 표시 (라이브 스케일 렌더의 vw 왜곡·잘림 방지).
                        썸네일이 없으면 기존 라이브 프리뷰로 폴백 */}
                    {savedTemplateData.thumbnailUrl ? (
                      <img
                        src={savedTemplateData.thumbnailUrl}
                        alt="인증서 미리보기"
                        className="w-full rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="overflow-hidden" style={{ height: isPortraitTemplate(savedTemplateData.templateType || 1) ? '500px' : '353.5px' }}>
                        <div className="scale-[0.5] origin-top-left" style={{ width: '200%' }}>
                          <CertificatePreview
                            templateType={savedTemplateData.templateType || 1}
                            borderColor={savedTemplateData.borderColor}
                            borderThickness={savedTemplateData.borderThickness}
                            logoUrl={savedTemplateData.logoUrl}
                            logoPosition={savedTemplateData.logoPosition}
                            logoSize={savedTemplateData.logoSize}
                            onLogoPositionChange={() => {}}
                            onLogoSizeChange={() => {}}
                            signatureUrl={savedTemplateData.signatureUrl}
                            signatureUrl2={savedTemplateData.signatureUrl2}
                            signaturePosition={savedTemplateData.signaturePosition}
                            signatureSize={savedTemplateData.signatureSize}
                            onSignaturePositionChange={() => {}}
                            onSignatureSizeChange={() => {}}
                            courseName={savedTemplateData.courseName}
                            signerName={savedTemplateData.signerName}
                            signerTitle={savedTemplateData.signerTitle}
                            signerName2={savedTemplateData.signerName2}
                            totalHours={(formData.offlineHours || 0) + (formData.onlineHours || 0)}
                            captureMode={true}
                          />
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-600 mt-2 text-center">
                      수강생이 수업을 완료하면 자동으로 인증서가 발급됩니다
                    </p>
                  </div>
                )}

                {/* 인증서 템플릿 버튼 */}
                {!savedTemplateData && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                      인증서 템플릿 <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleOpenTemplateModal}
                      className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors flex items-center justify-center gap-2 text-gray-700 hover:text-primary text-sm"
                    >
                      <Award className="w-4 h-4" />
                      인증서 템플릿 만들기
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 버튼 */}
          <div className="border-t border-gray-200 p-5 bg-gray-50 flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/provider/posts')}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors font-medium text-sm"
              disabled={loading}
            >
              취소
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  등록 중...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  수업 등록
                </>
              )}
            </button>
          </div>
        </form>

            {/* 썸네일 크롭 모달 */}
            {isThumbnailCropModalOpen && tempThumbnailUrl && (
              <ClassThumbnailCropModal
                imageUrl={tempThumbnailUrl}
                onClose={() => {
                  setIsThumbnailCropModalOpen(false);
                  if (tempThumbnailUrl) {
                    URL.revokeObjectURL(tempThumbnailUrl);
                    setTempThumbnailUrl(null);
                  }
                }}
                onSave={handleSaveThumbnail}
                isSaving={isSavingThumbnail}
              />
            )}

            {/* 인증서 템플릿 모달 */}
            <CertificateTemplateModal
              isOpen={isTemplateModalOpen}
              onClose={() => setIsTemplateModalOpen(false)}
              onSave={handleSaveTemplate}
              existingTemplateId={certificateTemplateId}
              initialData={savedTemplateData}
              totalHours={(formData.offlineHours || 0) + (formData.onlineHours || 0)}
            />
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
